import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery
} from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { toast } from 'sonner'

import { api } from '@/api'
import { AUTH_ACCESS_TOKEN_STORAGE_KEY } from '@/lib/axios'
import { delay } from '@/lib/utils'

interface roleName {
  roleName:
    | 'super_admin'
    | 'admin_pusat'
    | 'admin_provinsi'
    | 'admin_kabupaten'
    | 'admin_kecamatan'
    | 'admin_bpp'
    | 'penyuluh'
}

export function useAuth() {
  const queryClient = useQueryClient()

  const hasToken =
    typeof window !== 'undefined' &&
    !!localStorage.getItem(AUTH_ACCESS_TOKEN_STORAGE_KEY)

  const { data: profile } = useSuspenseQuery({
    ...api.auth.getProfile(),
    refetchOnWindowFocus: false
  })

  const navigate = useNavigate()
  const searchParams = useSearch({ strict: false })
  const redirectUrl =
    new URLSearchParams(searchParams as Record<string, string>).get(
      'redirect_to'
    ) ?? '/dashboard'

  const {
    mutate: signIn,
    mutateAsync: signInAsync,
    ...signInState
  } = useMutation({
    ...api.auth.signIn(queryClient),
    onMutate: () => {
      toast.loading('Sedang melakukan proses autentikasi...')
    },
    onSuccess: data => {
      toast.dismiss()

      // Check if user needs to change password
      const user = data.data?.user
      const passwordChangedAt = user?.password_changed_at

      if (user && !passwordChangedAt) {
        // Don't show success toast or redirect yet
        // Let the component handle the dialog
        return
      }

      toast.success('Proses autentikasi berhasil. Mengarahkan ke aplikasi...')
      delay(500).then(() => navigate({ to: redirectUrl }))
    },
    onError: e => {
      toast.dismiss()
      toast.error(e.message)
    }
  })

  const {
    mutate: signOut,
    mutateAsync: _signOutAsync,
    ...signOutState
  } = useMutation({
    ...api.auth.signOut(queryClient),
    onMutate: () => {
      toast.loading('Logging out...')
    },
    onSuccess: () => {
      toast.dismiss()
      toast.success('Logout berhasil.')
      delay(500).then(() => {
        window.location.href = '/'
      })
    },
    onError: e => {
      toast.dismiss()
      toast.error(e.message)
    }
  })

  const { data: rolesData } = useQuery({
    ...api.pengguna.getAllRoles(),
    enabled: hasToken,
    retry: false,
    refetchOnWindowFocus: false
  })
  const roleIdByRoleName = ({ roleName }: roleName) => {
    return rolesData?.data?.find(role => role.name === roleName)?.id
  }

  return {
    profile,
    signIn,
    signInAsync,
    signInState,
    signOut,
    signOutState,
    roleIdByRoleName
  }
}
